import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import './style.css';

// Canvas로 크롭된 이미지를 Blob으로 변환
function getCroppedImg(imageSrc, pixelCrop) {
    return new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = pixelCrop.width;
            canvas.height = pixelCrop.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                0, 0, pixelCrop.width, pixelCrop.height,
            );
            canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
        };
        image.src = imageSrc;
    });
}

const PLATFORMS = [
    { value: 'windows', label: 'Windows' },
    { value: 'mac', label: 'macOS' },
    { value: 'linux', label: 'Linux' },
    { value: 'ios', label: 'iOS (iPhone)' },
    { value: 'android', label: 'Android' },
];

const PRODUCTS = [
    { value: 'antstalk', label: 'AntsTalk' },
    { value: 'antsmeeting', label: 'AntsMeeting' },
    { value: 'nodemaster_local', label: 'NodeMaster (로컬)' },
    { value: 'nodemaster_teams', label: 'NodeMaster (Teams)' },
    { value: 'ganttchart', label: 'GanttChart' },
];

const PRODUCT_BADGE = {
    antstalk:         { label: 'Talk',       bg: 'rgba(1, 188, 214, 0.15)',  color: '#01BCD6' },
    antsmeeting:      { label: 'Meeting',    bg: 'rgba(214, 130, 1, 0.15)',  color: '#D6A001' },
    nodemaster_local: { label: 'Node Local', bg: 'rgba(167, 139, 250, 0.15)', color: '#a78bfa' },
    nodemaster_teams: { label: 'Node Teams', bg: 'rgba(96, 165, 250, 0.15)',  color: '#60a5fa' },
    ganttchart:       { label: 'Gantt',      bg: 'rgba(52, 211, 153, 0.15)',  color: '#34d399' },
    // 기존 데이터 호환
    nodemaster:       { label: 'Node',       bg: 'rgba(167, 139, 250, 0.15)', color: '#a78bfa' },
};

export default function DownloadAdmin() {
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('releases');

    // === 릴리스 상태 ===
    const [releases, setReleases] = useState([]);
    const [product, setProduct] = useState('antstalk');
    const [platform, setPlatform] = useState('windows');
    const [version, setVersion] = useState('');
    const [releaseNote, setReleaseNote] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);

    // === 슬라이드 상태 ===
    const [slides, setSlides] = useState([]);
    const [slideLabel, setSlideLabel] = useState('');
    const [slideFile, setSlideFile] = useState(null);
    const [slideUploading, setSlideUploading] = useState(false);

    // === 크롭 상태 ===
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImage, setCropImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedPreview, setCroppedPreview] = useState(null);

    useEffect(() => {
        fetch('/api/check-session', { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                if (data.success && data.user) {
                    const account = data.user.user_account || data.user.userId;
                    if (account === 'admin@antsnest.co.kr') {
                        setIsAdmin(true);
                        loadReleases();
                        loadSlides();
                    } else {
                        navigate('/Download');
                    }
                } else {
                    navigate('/Download');
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                navigate('/Download');
            });
    }, [navigate]);

    // === 릴리스 함수 ===
    const loadReleases = () => {
        fetch('/api/admin/releases', { credentials: 'include' })
            .then(res => res.json())
            .then(data => { if (data.success) setReleases(data.releases); })
            .catch(() => {});
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 4000);
    };

    const handleUpload = async () => {
        if (!version.trim()) return showMessage('error', '버전을 입력하세요.');
        if (!file) return showMessage('error', '파일을 선택하세요.');
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('files', file);
            const uploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=biz_antsnest', {
                method: 'POST', body: formData,
            });
            const uploadData = await uploadRes.json();
            if (!uploadData.files || !uploadData.files[0]) throw new Error('파일 업로드 실패');
            const downloadUrl = uploadData.files[0].url;

            const saveRes = await fetch('/api/admin/releases', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    platform, version: version.trim(), download_url: downloadUrl,
                    file_name: file.name, file_size: file.size,
                    release_note: releaseNote.trim() || null,
                    product,
                }),
            });
            const saveData = await saveRes.json();
            if (saveData.success) {
                showMessage('success', `${platform} v${version} 릴리스가 등록되었습니다.`);
                setVersion(''); setReleaseNote(''); setFile(null);
                const fileInput = document.getElementById('releaseFile');
                if (fileInput) fileInput.value = '';
                loadReleases();
            } else throw new Error(saveData.message);
        } catch (err) {
            showMessage('error', err.message || '업로드 중 오류가 발생했습니다.');
        } finally { setUploading(false); }
    };

    const handleToggle = async (id) => {
        await fetch(`/api/admin/releases/${id}/toggle`, { method: 'PUT', credentials: 'include' });
        loadReleases();
    };

    const handleDelete = async (id, fileName) => {
        if (!window.confirm(`"${fileName}" 릴리스를 삭제하시겠습니까?`)) return;
        await fetch(`/api/admin/releases/${id}`, { method: 'DELETE', credentials: 'include' });
        loadReleases();
        showMessage('success', '삭제되었습니다.');
    };

    // === 크롭 함수 ===
    const handleFileSelect = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        setCropImage(url);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setCroppedPreview(null);
        setSlideFile(null);
        setCropModalOpen(true);
    };

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCropConfirm = async () => {
        if (!cropImage || !croppedAreaPixels) return;
        const blob = await getCroppedImg(cropImage, croppedAreaPixels);
        const file = new File([blob], 'cropped-slide.png', { type: 'image/png' });
        setSlideFile(file);
        setCroppedPreview(URL.createObjectURL(blob));
        setCropModalOpen(false);
    };

    const handleCropCancel = () => {
        setCropModalOpen(false);
        setCropImage(null);
        const fi = document.getElementById('slideFile');
        if (fi) fi.value = '';
    };

    // === 슬라이드 함수 ===
    const loadSlides = () => {
        fetch('/api/admin/slides', { credentials: 'include' })
            .then(res => res.json())
            .then(data => { if (data.success) setSlides(data.slides); })
            .catch(() => {});
    };

    const handleSlideUpload = async () => {
        if (!slideLabel.trim()) return showMessage('error', '라벨을 입력하세요.');
        if (!slideFile) return showMessage('error', '이미지를 선택하세요.');
        setSlideUploading(true);
        try {
            const formData = new FormData();
            formData.append('files', slideFile);
            const uploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=biz_antsnest', {
                method: 'POST', body: formData,
            });
            const uploadData = await uploadRes.json();
            if (!uploadData.files || !uploadData.files[0]) throw new Error('이미지 업로드 실패');

            const saveRes = await fetch('/api/admin/slides', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label: slideLabel.trim(), image_url: uploadData.files[0].url }),
            });
            const saveData = await saveRes.json();
            if (saveData.success) {
                showMessage('success', `"${slideLabel}" 슬라이드가 추가되었습니다.`);
                setSlideLabel(''); setSlideFile(null); setCroppedPreview(null); setCropImage(null);
                const fi = document.getElementById('slideFile');
                if (fi) fi.value = '';
                loadSlides();
            } else throw new Error(saveData.message);
        } catch (err) {
            showMessage('error', err.message || '업로드 중 오류가 발생했습니다.');
        } finally { setSlideUploading(false); }
    };

    const handleSlideToggle = async (id) => {
        await fetch(`/api/admin/slides/${id}/toggle`, { method: 'PUT', credentials: 'include' });
        loadSlides();
    };

    const handleSlideHide = async (id, label) => {
        if (!window.confirm(`"${label}" 슬라이드를 숨기시겠습니까?`)) return;
        try {
            await fetch(`/api/admin/slides/${id}/toggle`, { method: 'PUT', credentials: 'include' });
            loadSlides();
            showMessage('success', `"${label}" 슬라이드가 숨겨졌습니다.`);
        } catch (err) {
            showMessage('error', '숨기기 실패');
        }
    };

    const handleMoveSlide = async (index, direction) => {
        const newSlides = [...slides];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newSlides.length) return;

        [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
        const orders = newSlides.map((s, i) => ({ id: s.id, sort_order: i }));

        try {
            await fetch('/api/admin/slides/reorder', {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders }),
            });
            loadSlides();
        } catch (err) {
            showMessage('error', '순서 변경 실패');
        }
    };

    const formatSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('ko-KR') + ' ' + d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) return <div className="DownloadContents"><div className="HomeContainer" style={{ padding: '100px 0', textAlign: 'center', color: '#fff' }}>로딩 중...</div></div>;
    if (!isAdmin) return null;

    return (
        <div className="DownloadContents">
            <div className="HomeContainer">
                {/* 헤더 */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '40px 0 20px' }}>
                    <h1 style={{ color: '#01BCD6', fontSize: '2rem', margin: 0 }}>관리자</h1>
                    <button className="AdminBtn" onClick={() => navigate('/Download')}>
                        ← 다운로드 페이지로
                    </button>
                </div>

                {/* 탭 */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 30, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    {[
                        { key: 'releases', label: '릴리스 관리' },
                        { key: 'slides', label: '슬라이드 관리' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                background: 'none',
                                border: 'none',
                                borderBottom: activeTab === tab.key ? '2px solid #01BCD6' : '2px solid transparent',
                                color: activeTab === tab.key ? '#01BCD6' : 'rgba(255,255,255,0.4)',
                                padding: '12px 24px',
                                fontSize: 15,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* 알림 메시지 */}
                {message && (
                    <div style={{
                        padding: '14px 20px', borderRadius: 10, marginBottom: 24,
                        background: message.type === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: message.type === 'success' ? '#4ade80' : '#f87171',
                        border: `1px solid ${message.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                        fontSize: 14,
                    }}>
                        {message.text}
                    </div>
                )}

                {/* ============ 릴리스 관리 탭 ============ */}
                {activeTab === 'releases' && (
                    <>
                        {/* 업로드 폼 */}
                        <div style={cardStyle}>
                            <h2 style={sectionTitle}>새 릴리스 업로드</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                                <div>
                                    <label style={labelStyle}>제품</label>
                                    <select value={product} onChange={e => setProduct(e.target.value)} style={inputStyle}>
                                        {PRODUCTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>플랫폼</label>
                                    <select value={platform} onChange={e => setPlatform(e.target.value)} style={inputStyle}>
                                        {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>버전</label>
                                    <input type="text" placeholder="1.0.0" value={version} onChange={e => setVersion(e.target.value)} style={inputStyle} />
                                </div>
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>파일 선택</label>
                                <input id="releaseFile" type="file" onChange={e => setFile(e.target.files[0])} style={{ ...inputStyle, padding: '10px 12px' }} />
                                {file && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 4, display: 'block' }}>{file.name} ({formatSize(file.size)})</span>}
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>릴리스 노트 (선택)</label>
                                <textarea placeholder="변경 사항을 입력하세요..." value={releaseNote} onChange={e => setReleaseNote(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                            </div>
                            <button className="DownloadBtn" onClick={handleUpload} disabled={uploading} style={{ opacity: uploading ? 0.6 : 1 }}>
                                {uploading ? '업로드 중...' : '릴리스 등록'}
                            </button>
                        </div>

                        {/* 릴리스 목록 */}
                        <div style={{ ...cardStyle, background: 'rgba(22, 47, 82, 0.4)', marginBottom: 60 }}>
                            <h2 style={sectionTitle}>등록된 릴리스 ({releases.length})</h2>
                            {releases.length === 0 ? (
                                <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 40 }}>등록된 릴리스가 없습니다.</p>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                                {['제품', '플랫폼', '버전', '파일명', '크기', '상태', '등록일', ''].map(h => (
                                                    <th key={h} style={thStyle}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {releases.map(r => (
                                                <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <td style={tdStyle}>{(() => {
                                                        const b = PRODUCT_BADGE[r.product] || { label: r.product, bg: badgeStyle.background, color: badgeStyle.color };
                                                        return <span style={{ ...badgeStyle, background: b.bg, color: b.color }}>{b.label}</span>;
                                                    })()}</td>
                                                    <td style={tdStyle}><span style={badgeStyle}>{r.platform}</span></td>
                                                    <td style={tdStyle}><span style={{ color: '#fff', fontWeight: 600 }}>v{r.version}</span></td>
                                                    <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        <a href={r.download_url} target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: 13 }}>{r.file_name || 'Link'}</a>
                                                    </td>
                                                    <td style={tdStyle}><span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>{formatSize(r.file_size)}</span></td>
                                                    <td style={tdStyle}>
                                                        <button onClick={() => handleToggle(r.id)} style={toggleBtnStyle(r.is_active)}>{r.is_active ? '활성' : '비활성'}</button>
                                                    </td>
                                                    <td style={tdStyle}><span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{formatDate(r.created_at)}</span></td>
                                                    <td style={tdStyle}>
                                                        <button onClick={() => handleDelete(r.id, r.file_name || r.version)} style={deleteBtnStyle}>삭제</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* ============ 슬라이드 관리 탭 ============ */}
                {activeTab === 'slides' && (
                    <>
                        {/* 슬라이드 업로드 폼 */}
                        <div style={cardStyle}>
                            <h2 style={sectionTitle}>새 슬라이드 이미지 추가</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                <div>
                                    <label style={labelStyle}>라벨 (표시 이름)</label>
                                    <input type="text" placeholder="예: NewService" value={slideLabel} onChange={e => setSlideLabel(e.target.value)} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>이미지 파일 (GIF/PNG/JPG)</label>
                                    <input id="slideFile" type="file" accept="image/*" onChange={handleFileSelect} style={{ ...inputStyle, padding: '10px 12px' }} />
                                </div>
                            </div>
                            {croppedPreview && (
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ ...labelStyle, marginBottom: 8 }}>크롭 결과</label>
                                    <img
                                        src={croppedPreview}
                                        alt="cropped preview"
                                        style={{ maxWidth: 260, maxHeight: 160, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)' }}
                                    />
                                    <button onClick={() => { setCropModalOpen(true); setCrop({ x: 0, y: 0 }); setZoom(1); }}
                                        style={{ ...deleteBtnStyle, color: '#01BCD6', marginLeft: 12 }}>
                                        다시 크롭
                                    </button>
                                </div>
                            )}
                            <button className="DownloadBtn" onClick={handleSlideUpload} disabled={slideUploading} style={{ opacity: slideUploading ? 0.6 : 1 }}>
                                {slideUploading ? '업로드 중...' : '슬라이드 추가'}
                            </button>
                        </div>

                        {/* 슬라이드 목록 */}
                        <div style={{ ...cardStyle, background: 'rgba(22, 47, 82, 0.4)', marginBottom: 60 }}>
                            <h2 style={sectionTitle}>슬라이드 이미지 ({slides.length})</h2>
                            {slides.length === 0 ? (
                                <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 40 }}>등록된 슬라이드가 없습니다.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {slides.map((s, idx) => (
                                        <div key={s.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 16,
                                            background: 'rgba(0, 15, 35, 0.4)', borderRadius: 12,
                                            padding: '12px 16px',
                                            border: `1px solid ${s.is_active ? 'rgba(1, 188, 214, 0.15)' : 'rgba(255,255,255,0.05)'}`,
                                            opacity: s.is_active ? 1 : 0.5,
                                        }}>
                                            {/* 순서 */}
                                            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, width: 24, textAlign: 'center' }}>{idx + 1}</span>

                                            {/* 썸네일 */}
                                            <img
                                                src={s.image_url}
                                                alt={s.label}
                                                style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}
                                            />

                                            {/* 라벨 + 기본 뱃지 */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                                                    {s.label}
                                                    {s.is_default && <span style={{ ...badgeStyle, marginLeft: 8, fontSize: 10 }}>기본</span>}
                                                </div>
                                                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {s.image_url}
                                                </div>
                                            </div>

                                            {/* 순서 버튼 */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <button onClick={() => handleMoveSlide(idx, 'up')} disabled={idx === 0}
                                                    style={{ ...arrowBtnStyle, opacity: idx === 0 ? 0.2 : 1 }}>▲</button>
                                                <button onClick={() => handleMoveSlide(idx, 'down')} disabled={idx === slides.length - 1}
                                                    style={{ ...arrowBtnStyle, opacity: idx === slides.length - 1 ? 0.2 : 1 }}>▼</button>
                                            </div>

                                            {/* 활성/비활성 */}
                                            <button onClick={() => handleSlideToggle(s.id)} style={toggleBtnStyle(s.is_active)}>
                                                {s.is_active ? '활성' : '비활성'}
                                            </button>

                                            {/* 숨기기 (활성 상태일 때만 표시) */}
                                            {s.is_active ? (
                                                <button onClick={() => handleSlideHide(s.id, s.label)} style={deleteBtnStyle}>숨기기</button>
                                            ) : (
                                                <span style={{ width: 46 }} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* 크롭 모달 */}
            {cropModalOpen && cropImage && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.85)', zIndex: 9999,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                        슬라이드에 표시할 영역을 지정하세요 (16:10)
                    </div>
                    <div style={{ position: 'relative', width: '80vw', maxWidth: 600, height: '50vh', background: '#111', borderRadius: 12, overflow: 'hidden' }}>
                        <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={16 / 10}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20 }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>줌</span>
                        <input type="range" min={1} max={3} step={0.1} value={zoom}
                            onChange={e => setZoom(Number(e.target.value))}
                            style={{ width: 160 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                        <button onClick={handleCropCancel}
                            style={{ padding: '10px 28px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'none', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
                            취소
                        </button>
                        <button onClick={handleCropConfirm}
                            style={{ padding: '10px 28px', borderRadius: 8, border: 'none', background: '#01BCD6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                            확인
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const cardStyle = {
    background: 'rgba(22, 47, 82, 0.6)',
    borderRadius: 16,
    padding: '32px',
    border: '1px solid rgba(1, 188, 214, 0.15)',
    marginBottom: 30,
};

const sectionTitle = { color: '#fff', fontSize: '1.3rem', margin: '0 0 24px' };

const labelStyle = {
    display: 'block', color: 'rgba(255,255,255,0.6)',
    fontSize: 13, marginBottom: 6, fontWeight: 500,
};

const inputStyle = {
    width: '100%', background: 'rgba(0, 15, 35, 0.6)',
    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
    padding: '12px 14px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

const thStyle = {
    textAlign: 'left', color: 'rgba(255,255,255,0.4)',
    fontSize: 12, fontWeight: 500, padding: '10px 12px', textTransform: 'uppercase',
};

const tdStyle = { padding: '12px', verticalAlign: 'middle' };

const badgeStyle = {
    background: 'rgba(1, 188, 214, 0.15)', color: '#01BCD6',
    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
};

const toggleBtnStyle = (active) => ({
    background: active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
    color: active ? '#4ade80' : '#f87171',
    border: 'none', padding: '4px 12px', borderRadius: 6,
    fontSize: 12, cursor: 'pointer', fontWeight: 600,
});

const deleteBtnStyle = {
    background: 'none', border: 'none', color: '#f87171',
    cursor: 'pointer', fontSize: 13, padding: '4px 8px',
};

const arrowBtnStyle = {
    background: 'rgba(255,255,255,0.08)', border: 'none',
    color: 'rgba(255,255,255,0.5)', padding: '2px 8px', borderRadius: 4,
    cursor: 'pointer', fontSize: 10,
};
